import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CloudUpload, Loader2, CheckCircle2, AlertCircle, Database, Zap } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface SyncResult {
    success: boolean;
    results?: Record<string, { success?: boolean; error?: string; documentCount?: number; sizeBytes?: number }>;
    error?: string;
}

export default function AdminPerformance() {
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

    const handleSyncToStorage = async () => {
        if (!confirm('Sync bulk data collections to Firebase Storage? This will update the JSON files used for fast loading.')) {
            return;
        }

        setIsSyncing(true);
        setSyncResult(null);

        try {
            const functions = getFunctions();
            const syncBulkData = httpsCallable<any, SyncResult>(functions, 'syncBulkData', {
                timeout: 600000 // 10 minutes
            });

            console.log('Starting bulk data sync...');
            const response = await syncBulkData({});
            console.log('Sync complete:', response.data);

            setSyncResult(response.data);
        } catch (error: any) {
            console.error('Sync failed:', error);
            setSyncResult({
                success: false,
                error: error.message || 'Unknown error occurred'
            });
        } finally {
            setIsSyncing(false);
        }
    };

    const getCollectionStatus = (collectionName: string, result: any) => {
        if (result.error) {
            return <Badge variant="destructive" className="text-xs">Error</Badge>;
        }
        if (result.success) {
            return <Badge variant="default" className="text-xs bg-green-600">Synced</Badge>;
        }
        return <Badge variant="secondary" className="text-xs">Unknown</Badge>;
    };

    const formatBytes = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Performance & Storage
                </CardTitle>
                <CardDescription>
                    Manage bulk data synchronization for optimized loading performance
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Sync to Storage Section */}
                <div className="space-y-4">
                    <div>
                        <h3 className="text-lg font-semibold mb-2">Bulk Data Sync</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Sync large aggregate collections to Firebase Storage as compressed JSON files.
                            This enables fast single-request loading for passages, translations, people, places, and historical events.
                        </p>
                    </div>

                    <div className="bg-muted/30 p-4 rounded-lg border space-y-2">
                        <div className="flex items-center gap-2 text-sm font-medium">
                            <Database className="h-4 w-4" />
                            <span>Collections Synced to Storage:</span>
                        </div>
                        <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 ml-6">
                            <li>passages (~15MB → ~2MB gzipped)</li>
                            <li>people (~5MB → ~600KB gzipped)</li>
                            <li>places (~2MB → ~300KB gzipped)</li>
                            <li>historicalEvents (~2MB → ~300KB gzipped)</li>
                            <li>literaryFeaturesByPassage (for search)</li>
                        </ul>
                        <p className="text-xs text-muted-foreground mt-2 italic">
                            Note: Translation data is served from individual files (csb.json, niv.json, etc.). Book-level collections (66 docs each) remain in Firestore for simpler maintenance.
                        </p>
                    </div>

                    <Button
                        onClick={handleSyncToStorage}
                        disabled={isSyncing}
                        size="lg"
                        className="w-full"
                    >
                        {isSyncing ? (
                            <>
                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                Syncing to Storage...
                            </>
                        ) : (
                            <>
                                <CloudUpload className="mr-2 h-5 w-5" />
                                Sync to Storage
                            </>
                        )}
                    </Button>
                </div>

                {/* Sync Results */}
                {syncResult && (
                    <div className="space-y-4">
                        <Alert variant={syncResult.success ? 'default' : 'destructive'}>
                            {syncResult.success ? (
                                <CheckCircle2 className="h-4 w-4" />
                            ) : (
                                <AlertCircle className="h-4 w-4" />
                            )}
                            <AlertTitle>{syncResult.success ? 'Sync Completed' : 'Sync Failed'}</AlertTitle>
                            <AlertDescription>
                                {syncResult.success ? (
                                    'All collections have been synced to Firebase Storage.'
                                ) : (
                                    syncResult.error || 'An error occurred during sync.'
                                )}
                            </AlertDescription>
                        </Alert>

                        {syncResult.results && (
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted">
                                        <tr>
                                            <th className="text-left p-3 font-medium">Collection</th>
                                            <th className="text-center p-3 font-medium">Status</th>
                                            <th className="text-right p-3 font-medium">Documents</th>
                                            <th className="text-right p-3 font-medium">Size</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(syncResult.results).map(([collection, result]) => (
                                            <tr key={collection} className="border-t">
                                                <td className="p-3 font-mono text-xs">{collection}</td>
                                                <td className="p-3 text-center">
                                                    {getCollectionStatus(collection, result)}
                                                </td>
                                                <td className="p-3 text-right">
                                                    {result.documentCount?.toLocaleString() || '—'}
                                                </td>
                                                <td className="p-3 text-right font-mono text-xs">
                                                    {result.sizeBytes ? formatBytes(result.sizeBytes) : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
